import { UrlObject } from 'url'

import React, { useMemo } from 'react'
import type { Hit } from 'react-instantsearch-core'

import useAugmentedRouter from 'hooks/useAugmentedRouter'

import CustomLink from 'components/Link/CustomLink'
import ResultCarousel from 'components/Carousels/ResultCarousel'
import FavoriteButton from 'components/Favorites/FavoriteButton'
import StarRatings from 'components/StarRatings/StarRatings'

import style from './FavoriteListingCard.module.scss'

import { avgNightCopy } from 'config/Search'

import { setPrice, calcRating } from 'utils/Strings'
import { buildListingLink } from 'utils/Listings'
import pluralize from 'utils/strings/pluralize'

import ComingSoon from 'assets/icons/icon-coming-soon-md.svg'

import { AlgoliaListing } from 'types/externalData'
import type { FavoriteListing } from 'types/favorites'

type FavoriteListingCardProps = {
  listing: FavoriteListing
  isFaded: boolean
}

const FavoriteListingCard: React.FC<FavoriteListingCardProps> = ({
  listing,
  isFaded,
}) => {
  const { query } = useAugmentedRouter()

  const calculatedRating = calcRating(listing['Average Rating']?.toString())

  const renderImages = useMemo(
    () => (
      <div className={style.imagesWrapper}>
        {listing.images?.length ? (
          <ResultCarousel
            hit={listing as Hit<AlgoliaListing>}
            mapDisplay={false}
          />
        ) : (
          <div className={style.comingSoon}>
            <ComingSoon className={style.comingSoonImg} />
            <span>Coming Soon!</span>
          </div>
        )}
        <span className={style.resultImageType}>
          {listing['Property Type']}
        </span>
      </div>
    ),
    [listing],
  )

  const listingLinkFromUrl: UrlObject = useMemo(
    () => buildListingLink(listing as AlgoliaListing, query),
    [listing, query],
  )

  // TODO: Use LOS pricing (WT-2381)?
  const renderListing = useMemo(
    () => (
      <>
        <div className={style.resultDetailsDescription}>
          <span className={style.resultType}>{listing['Property Type']}</span>
          <div className={style.resultDetailsDescriptionChild}>
            <span className={style.resultHeadline}>{listing['Headline']}</span>
          </div>
          <span className={style.resultLocation}>
            {listing['City']}, {listing['State']}{' '}
            {listing['Country'] !== 'US' && listing['Country']}
          </span>
        </div>
        <div className={style.resultDetailInfo}>
          <span>Sleeps {listing['Max Occupancy']}</span>
          <span>{`${listing['Bedrooms']} ${pluralize(
            'Bedroom',
            listing['Bedrooms'],
          )}`}</span>
          <span>{`${listing['Bathrooms']} ${pluralize(
            'Bathroom',
            listing['Bathrooms'],
          )}`}</span>
        </div>
        <div
          className={`${style.resultDetailBottom} ${style.favoritesResultDetailBottom}`}
        >
          {listing['Average Per Night'] && (
            <span className={style.avgPrice}>
              <b className={style.boldPrice}>
                {setPrice(listing['Average Per Night'])}
              </b>{' '}
              <span className={style.boldPrice}>{avgNightCopy}</span>
            </span>
          )}
          {calculatedRating > 0 && (
            <div className={style.resultDetailRating}>
              <div className={style.starRating}>
                <span>{calculatedRating}</span>
                <StarRatings
                  numberOfStars={5}
                  rating={calculatedRating}
                  starDimension="18px"
                  starRatedColor="#F9A01F"
                  starSpacing="1px"
                  uniqueId="favoritesRating"
                />
              </div>
              <span className={style.reviews}>
                ({calcRating(listing['Number of Reviews'].toString())})
              </span>
            </div>
          )}
        </div>
      </>
    ),
    [listing, calculatedRating],
  )

  return (
    listing && (
      <div
        className={`${style.card} ${style.result} ${
          isFaded ? style.faded : ''
        }`}
        id={`favoriteListingCard-${listing.objectID}`}
      >
        <CustomLink
          className={`${style.resultImage}`}
          href={listingLinkFromUrl}
          id={`listing_${listing['objectID']}`}
          target="_blank"
        >
          {renderImages}
        </CustomLink>
        <FavoriteButton
          container="favoritesPage"
          listing={listing as Hit<AlgoliaListing>}
        />
        <CustomLink
          className={style.resultDetails}
          href={listingLinkFromUrl}
          id={`listing_${listing['objectID']}`}
          target="_blank"
        >
          {renderListing}
        </CustomLink>
      </div>
    )
  )
}

export default FavoriteListingCard
